import { useState, type ReactNode, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextStore } from '.';
import { useSignOut } from '../hooks/auth/useSignOut';
import { setRefreshToken } from '../utils/tokenManager';
import { isImpersonating, getImpersonationUser } from '../utils/impersonationSession';
import { onSessionEvicted } from '../utils/sessionEvictionEvent';
import SessionEvictedModal from '../components/SessionEvictedModal';
import { FullScreenLoader } from '../components/ui/FullScreenLoader';
import { analytics } from '../services/analytics';

const DEFAULT_DASHBOARD_SETTINGS = {
  showClock: false,
  showDate: false,
  showSeconds: false,
  timeFormat: '12h' as const,
};

export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const [clientUser, setClientUser] = useState<ContextState['clientUser']>(() => {
    if (isImpersonating()) {
      const impUser = getImpersonationUser();
      if (impUser) {
        return {
          id: impUser.userId,
          _id: impUser.userId,
          email: impUser.userEmail,
          firstName: impUser.userFirstName || '',
          lastName: impUser.userLastName || '',
          role: impUser.userRole,
          companyName: impUser.companyName || '',
          analyticsConsent: false,
          isWelcomeScreenClosed: true,
          settings: {
            dashboardSettings: DEFAULT_DASHBOARD_SETTINGS,
          },
        } as unknown as ClientUser;
      }
    }

    const storedUser = localStorage.getItem('clientUser');
    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch (error) {
      // Handle invalid JSON gracefully
      console.warn('Invalid JSON in localStorage for clientUser:', error);
      localStorage.removeItem('clientUser');
      return null;
    }
  });

  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [isSessionEvicted, setIsSessionEvicted] = useState(false);

  const { i18n } = useTranslation();

  // Listen for session eviction events from Apollo error link
  useEffect(() => {
    analytics.applyConsent(clientUser?.analyticsConsent, clientUser?.id);
  }, [clientUser?.analyticsConsent, clientUser?.id]);

  useEffect(() => {
    const cleanup = onSessionEvicted(() => {
      setIsSessionEvicted(true);
    });
    return cleanup;
  }, []);

  // Update language when user changes
  useEffect(() => {
    if (clientUser?.locale) {
      const language = clientUser.locale.toLowerCase();
      const supportedLanguage = ['en', 'es'].includes(language) ? language : 'en';
      i18n.changeLanguage(supportedLanguage);
    }
  }, [clientUser?.locale, i18n]);

  // Memoize signIn callback
  const signIn = useCallback(
    ({ token, refreshToken, user }: { token: string; refreshToken?: string; user: ClientUser }) => {
      // Ensure user has default dashboard settings if not provided
      // timeFormat is handled locally and not fetched from backend
      const userWithSettings = {
        ...user,
        analyticsConsent: isImpersonating() ? false : (user.analyticsConsent ?? null),
        isWelcomeScreenClosed: isImpersonating() ? true : (user.isWelcomeScreenClosed ?? false),
        settings: {
          ...user.settings,
          dashboardSettings: {
            ...DEFAULT_DASHBOARD_SETTINGS,
            ...user.settings?.dashboardSettings,
            // Always use local timeFormat setting, not from backend
            timeFormat: '12h' as const,
          },
        },
      };

      if (!isImpersonating()) {
        localStorage.setItem('clientAccessToken', token);
        localStorage.setItem('clientUser', JSON.stringify(userWithSettings));

        // Store refresh token for session persistence
        if (refreshToken) {
          setRefreshToken(refreshToken);
        }
      }

      setClientUser(userWithSettings);
    },
    []
  );

  // Update dashboard settings
  const updateDashboardSettings = useCallback(
    (settings: DashboardSettings) => {
      if (clientUser) {
        const updatedUser = {
          ...clientUser,
          settings: {
            ...clientUser.settings,
            dashboardSettings: settings,
          },
        };
        setClientUser(updatedUser);
        if (!isImpersonating()) {
          localStorage.setItem('clientUser', JSON.stringify(updatedUser));
        }
      }
    },
    [clientUser]
  );

  // Update user profile
  const updateUserProfile = useCallback(
    (updates: Partial<ClientUser>) => {
      if (clientUser) {
        const updatedUser = {
          ...clientUser,
          ...updates,
        };
        setClientUser(updatedUser);
        if (!isImpersonating()) {
          localStorage.setItem('clientUser', JSON.stringify(updatedUser));
        }
      }
    },
    [clientUser]
  );

  // Use the useSignOut hook with a callback to clear user state
  const {
    signOut,
    isLoading: signOutLoading,
    isSigningOut,
  } = useSignOut(() => {
    analytics.reset();
    setClientUser(null);
  });

  const contextStore: ContextState = useMemo(
    () => ({
      signIn,
      signOut,
      clientUser,
      signOutLoading,
      updateDashboardSettings,
      updateUserProfile,
      isAuthenticating,
      setIsAuthenticating,
    }),
    [
      signOut,
      clientUser,
      signIn,
      signOutLoading,
      updateDashboardSettings,
      updateUserProfile,
      isAuthenticating,
    ]
  );
  return (
    <ContextStore.Provider value={contextStore}>
      {children}
      {/* Logout replaces the document, but the request has to come back first.
          This covers that gap: otherwise the guards route to /signin in this
          document and the arriving one paints the same screen again. */}
      <FullScreenLoader isLoading={isSigningOut} />
      <SessionEvictedModal
        isOpen={isSessionEvicted}
        onReauthenticated={() => setIsSessionEvicted(false)}
        onSignOut={() => {
          setIsSessionEvicted(false);
          signOut();
        }}
      />
    </ContextStore.Provider>
  );
};
